import { useState } from "react";
import apiClient from "@/services/apiClient";
import ToastHelper from "@/utils/toastHelper";

export const useStudentBooking = (course, courseScheduleId) => {
  const [step, setStep] = useState("pay");
  const [student, setStudent] = useState({ firstName: "", lastName: "", email: "", mobile: "", dob: "" });
  const [processing, setProcessing] = useState(false);
  const [bookingResponse, setBookingResponse] = useState(null);

  const formatTime = (value) => {
    if (!value) return "";
    const [hours = "0", minutes = "0"] = String(value).split(":");
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);
    return date.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  };

  const getScheduleDate = (course) => {
    const rawDate = course?.ScheduleDate || course?.scheduleDate || course?.date;
    if (!rawDate) return null;
    const parsed = new Date(rawDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getScheduleTime = (course) => {
    const start = course?.StartTime || course?.startTime;
    const end = course?.EndTime || course?.endTime;
    if (start && end) return `${formatTime(start)} – ${formatTime(end)}`;
    return course?.time || "";
  };

  const getLocationText = (course) => {
    const campusName = course?.CampusName || course?.campusName || course?.location;
    const suburb = course?.Suburb || course?.suburb;
    const postcode = course?.PostCode || course?.postCode;
    return [campusName, suburb, postcode].filter(Boolean).join(", ");
  };

  const getTrainerName = (course) => course?.TrainerName || course?.trainerName || course?.trainer || "TBA";

  const courseFee = Number(course?.CourseFee ?? course?.courseFee ?? course?.price ?? 0);
  const subtotal = courseFee;
  const gst = 0;
  const discount = 0;
  const total = courseFee;
  const scheduleDate = getScheduleDate(course);
  const scheduleTime = getScheduleTime(course);
  const locationText = getLocationText(course);
  const trainerName = getTrainerName(course);

  const createPaymentIFrame = async (courseId, studentData) => {
    const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
    const externalId = `GIT-${courseId}-${Date.now()}`;
    const response = await apiClient.post(`${apiBaseUrl}/PayAdvantage/iframe`, {
      amount: Number(total.toFixed(2)),
      description: `GIT course booking - schedule ${courseId}`,
      externalId,
      firstName: studentData.firstName,
      lastName: studentData.lastName,
      email: studentData.email,
      mobileNumber: studentData.mobile,
      dateOfBirth: studentData.dob ? new Date(studentData.dob).toISOString() : null,
    });
    return response.data;
  };

  const submitBooking = async (studentData, courseId, paymentReference, paymentResponse) => {
    setProcessing(true);

    try {
      const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
      const payload = {
        FirstName: studentData.firstName,
        LastName: studentData.lastName,
        Email: studentData.email,
        MobileNumber: studentData.mobile,
        DateOfBirth: studentData.dob ? new Date(studentData.dob).toISOString() : null,
        CourseScheduleId: courseId,
        CourseFee: Number(courseFee.toFixed(2)),
        GST: Number(gst.toFixed(2)),
        Discount: Number(discount.toFixed(2)),
        TotalAmount: Number(total.toFixed(2)),
        PaymentReference: paymentReference,
        PaymentResponse: paymentResponse,
      };

      const response = await apiClient.post(`${apiBaseUrl}/StudentBooking`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setBookingResponse(response.data);
      setStep("done");

      if (response.data?.success === true && response.data?.statusCode === 1) {
        ToastHelper.success("Booking submitted successfully!");
      } else {
        ToastHelper.error(response.data?.message || "Unable to complete your booking.");
      }

      return response.data;
    } catch (error) {
      console.error("Failed to submit booking", error);
      const apiError = error?.response?.data;
      const validationErrors = apiError?.errors
        ? Object.values(apiError.errors).flat().join(" ")
        : "";
      const message =
        validationErrors ||
        apiError?.message ||
        apiError?.Message ||
        error?.message ||
        "Unable to submit booking right now.";
      const failedResponse = { success: false, statusCode: 0, message };
      setBookingResponse(failedResponse);
      setStep("done");
      ToastHelper.error(message);
      return failedResponse;
    } finally {
      setProcessing(false);
    }
  };

  return {
    // State
    step,
    setStep,
    student,
    setStudent,
    processing,
    discount,
    bookingResponse,

    // Calculated values
    courseFee,
    subtotal,
    gst,
    total,
    scheduleDate,
    scheduleTime,
    locationText,
    trainerName,

    // Methods
    createPaymentIFrame,
    submitBooking,
  };
};
